/**
 * Prepaid coverage status stat cards — active/expiring/scheduled/expired/
 * converted counts for a scope (every customer, or one), doubling as a
 * click-to-filter control for the PrepaidCoverageGrid beside it. Shared by
 * the staff-wide prepaid dashboard and the per-account prepaid subview.
 *
 * Contains:
 * - PrepaidStatusCards — stat-card row (default export)
 * - PREPAID_STATUS_CARD_COUNT — card count, for a caller's column geometry
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchPrepaidSummary } from '@sophocles/api/billing';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';

const SUMMARY_CARD_DEFS = [
  { key: 'active', labelKey: 'billing.prepaidCoverage.statusActive', color: 'var(--status-ok)' },
  { key: 'expiring', labelKey: 'billing.prepaidCoverage.statusExpiring', color: 'var(--status-warning)' },
  { key: 'scheduled', labelKey: 'billing.prepaidCoverage.statusScheduled', color: 'var(--status-info)' },
  { key: 'expired', labelKey: 'billing.prepaidCoverage.statusExpired', color: 'var(--status-bad)' },
  { key: 'converted', labelKey: 'billing.prepaidCoverage.statusConverted', color: 'var(--status-attention)' },
];

export const PREPAID_STATUS_CARD_COUNT = SUMMARY_CARD_DEFS.length;

/**
 * @param {Object} props
 * @param {number|null} [props.customerId] - Scopes the summary to one customer; null counts every customer.
 * @param {boolean} [props.requireCustomerId=false] - Skip fetching while `customerId` is null (a
 *   customer-scoped caller has nothing meaningful to show yet, vs. a global caller where null is a
 *   real "every customer" scope).
 * @param {string} props.statusFilter - The active status bucket, or '' for none.
 * @param {(status: string) => void} props.onStatusFilterChange - Toggles the clicked card's bucket.
 * @param {number} [props.reloadToken=0] - Bump to re-fetch without a param change (e.g. after the grid writes).
 * @param {Object} props.columns - StatCardGroup's responsive column counts.
 */
function PrepaidStatusCards({
  customerId,
  requireCustomerId = false,
  statusFilter,
  onStatusFilterChange,
  reloadToken = 0,
  columns,
}) {
  const { t } = useTranslation();
  const [summaryData, setSummaryData] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);

  const SUMMARY_CARDS = useMemo(() => SUMMARY_CARD_DEFS.map(def => ({ ...def, label: t(def.labelKey) })), [t]);

  const summaryParams = useMemo(() => (customerId ? { customer: customerId } : {}), [customerId]);

  useEffect(() => {
    if (requireCustomerId && customerId == null) return undefined;
    let cancelled = false;
    setSummaryLoading(true);
    fetchPrepaidSummary(summaryParams)
      .then(data => {
        if (!cancelled) setSummaryData(data);
      })
      .catch(() => {
        if (!cancelled) setSummaryData({});
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summaryParams, reloadToken, requireCustomerId, customerId]);

  const isCardActive = useCallback(card => statusFilter === card.key, [statusFilter]);
  const handleCardClick = useCallback(
    card => onStatusFilterChange(statusFilter === card.key ? '' : card.key),
    [statusFilter, onStatusFilterChange],
  );

  return (
    <StatCardGroup columns={columns} sx={{ marginBottom: 'var(--page-gap)' }}>
      {SUMMARY_CARDS.map(card => (
        <StatCard
          key={card.label}
          title={card.label}
          value={summaryLoading ? '—' : (summaryData[card.key] ?? 0)}
          color={card.color}
          loading={summaryLoading}
          active={isCardActive(card)}
          onClick={() => handleCardClick(card)}
        />
      ))}
    </StatCardGroup>
  );
}

export default PrepaidStatusCards;
